import type { ResidentPayDataDto } from '@/shared/api/modules/residents';
import { chartColor } from '@/shared/lib/chart-theme';
import {
  LengthOfStayScatterChart,
  Toggle,
  TooltipElement,
  Typography,
} from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import type { ChartData, ChartOptions } from 'chart.js';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';
import { useState } from 'react';

import {
  getPayAcrossCareLevelsQueryOptions,
  type PayDeviationBaseline,
} from '../api/get-pay-across-care-levels.query';

export type LosChartData = ResidentPayDataDto;

type LengthOfStayChartProps = {
  facilityId: string;
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

export const LengthOfStayChart: FC<LengthOfStayChartProps> = ({
  facilityId,
}) => {
  const [baseline, setBaseline] = useState<PayDeviationBaseline>('MEDIAN');

  const { data: responseData, isLoading } = useQuery(
    getPayAcrossCareLevelsQueryOptions({
      facilityId: parseInt(facilityId),
      baseline,
    })
  );

  const getColor = (y: number) => {
    if (y > 10) return chartColor('--color-polar-green-5');
    if (y < -10) return chartColor('--color-dust-red-5');
    return chartColor('--color-neutral-7');
  };

  const deterministicJitter = (index: number, total: number) => {
    const spread = 0.4;
    return (index / total - 0.1) * spread;
  };

  const labels = responseData?.labels ?? [];
  const dataGroups = responseData?.data ?? [];

  const transformedData = dataGroups.flatMap((level, levelIndex) =>
    level.map((resident, i) => ({
      x: levelIndex + 1 + deterministicJitter(i, level.length),
      y: resident.payPosition,
      meta: resident,
    }))
  );

  const chartData: ChartData<'scatter'> = {
    labels,
    datasets: [
      {
        pointRadius: 5,
        pointHoverRadius: 6,
        backgroundColor: (ctx) => getColor((ctx.raw as { y: number })?.y),
        data: transformedData,
      },
    ],
  };

  const normalizedValues = dataGroups.flat().map((d) => d.payPosition);

  const yAxisMax = Math.max(...normalizedValues, 10);
  const computedMax = Math.ceil(yAxisMax / 10) * 10 + 5;

  const yAxisMin = Math.min(...normalizedValues, -10);
  const computedMin = Math.floor(yAxisMin / 10) * 10 - 5;

  const padding = 0.5;
  const xAxisMax = labels.length + padding;

  const options: ChartOptions<'scatter'> = {
    scales: {
      x: {
        max: xAxisMax,
      },
      y: {
        max: computedMax,
        min: computedMin,
      },
    },
  };

  const baselineLabel = baseline === 'MEDIAN' ? 'Median' : 'Average';

  return (
    <div className="border-neutral-4 flex flex-col gap-1 border bg-white p-3">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-x-2">
          <Typography
            as="h5"
            variant="h5"
            weight="medium"
            className="text-neutral-12"
          >
            Resident Pay Across All Care Levels
          </Typography>
          <TooltipElement content="Comparison of individual resident payment rates against the median or average rate for their specific care level" />
        </div>

        <div className="flex items-center gap-x-6">
          <div className="flex items-center gap-x-1">
            <div className="bg-polar-green-5 h-2 w-2 rounded-full" />
            <Typography className="text-neutral-9">
              Above {baselineLabel}
            </Typography>
          </div>

          <div className="flex items-center gap-x-1">
            <div className="bg-neutral-7 h-2 w-2 rounded-full" />
            <Typography className="text-neutral-9">On Target</Typography>
          </div>

          <div className="flex items-center gap-x-1">
            <div className="bg-dust-red-5 h-2 w-2 rounded-full" />
            <Typography className="text-neutral-9">
              Below {baselineLabel}
            </Typography>
          </div>
        </div>

        <Toggle
          alwaysActive
          label="Average"
          leftLabel="Median"
          onChange={(e) => setBaseline(e.target.checked ? 'AVERAGE' : 'MEDIAN')}
        />
      </div>

      <div className="h-[330px]">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Typography className="text-neutral-7">Loading...</Typography>
          </div>
        ) : !responseData || labels.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Typography className="text-neutral-7">
              No data available
            </Typography>
          </div>
        ) : (
          <LengthOfStayScatterChart
            data={chartData}
            options={options}
            baselineLabel={baselineLabel}
          />
        )}
      </div>
    </div>
  );
};
